import type {AutoValue, Values} from '@wix/devzai-utils-common';
import {autoValueCreate, autoValueIsAutoValue} from '@wix/devzai-utils-common';
import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    doppeBymoPageVideoSourceResolveVideoUrl
} from '../../client-server-common';
import type {DoppeHideableValue} from '../../client-server-common/types/doppe-hideable-value';
import {
    doppeHideableValueCreateHidden,
    doppeHideableValueGetValue,
    doppeHideableValueIsVisible
} from '../../client-server-common/types/doppe-hideable-value';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import type {RichTextContent} from '@wix/devzai-utils-react';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import {doppeActionCtaButtonsResetSettingsForLock} from '../../client-server-common/types/doppe-action-cta-button';
import type {DoppeActionCtaButtonSettings} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration,
    getDefaultDoppeActionCtaButtonSettings
} from '../../client-server-common/types/doppe-action-cta-button-settings';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';

export const YoutubeEmbedType = {
    VideoUrl: 'VideoUrl',
    LatestVideo: 'LatestVideo',
} as const;

export const DoppeActionTypeYoutubeVideoURLAutoSource = {
    SameAsVideoURL: 'SameAsVideoURL',
} as const;

export type DoppeActionTypeYoutubeVideoURLAutoSource = Values<typeof DoppeActionTypeYoutubeVideoURLAutoSource>;

export namespace DoppeActionTypeYoutubeVideo {

    export type YoutubeEmbedType = Values<typeof YoutubeEmbedType>;


    export type DoppeActionTypeYoutubeVideoURLSource = AutoValue<DoppeActionTypeYoutubeVideoURLAutoSource> | string;

    export interface ActionSettings extends
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings {
        url: string;
        type: YoutubeEmbedType;
        title: DoppeHideableValue<string>;
        description: DoppeHideableValue<RichTextContent>;
        videoPlayerSettings: {
            autoplay: boolean;
            muteVideo: boolean;
        },
        videoImage: WixMediaResource | null;
    }

    export type DeprecatedFields = Partial<DoppeActionCtaButtonSettings> & {
        videoButtonText?: DoppeHideableValue<string>;
        videoButtonURL?: DoppeActionTypeYoutubeVideoURLSource;
    }

    export type PartialProps = Partial<ActionSettings> & DeprecatedFields;
}

export const doppeActionTypeYoutubeVideo = doppeActionTypeDefineMetadata<DoppeActionTypeYoutubeVideo.ActionSettings, DoppeActionTypeYoutubeVideo.PartialProps>({
    id: 'io.bymo.action/youtube-video',
    resolveActionSettings: action => {

        const {
            videoButtonText,
            videoButtonURL,
            ctaButton,
            ctaButtons,
            sections,
            ...restSettings
        } = action.settings;

        const buttonText = videoButtonText ?? doppeHideableValueCreateHidden('');

        return {
            url: '',
            type: YoutubeEmbedType.VideoUrl,
            videoPlayerSettings: {
                autoplay: true,
                muteVideo: false
            },
            title: doppeHideableValueCreateHidden(''),
            description: doppeHideableValueCreateHidden(''),
            videoButtonText: buttonText,
            videoImage: null,
            videoButtonURL: autoValueCreate(DoppeActionTypeYoutubeVideoURLAutoSource.SameAsVideoURL),
            ...getDefaultDoppeActionCtaButtonSettings({
                buttonText: doppeHideableValueGetValue(buttonText),
                showButton: doppeHideableValueIsVisible(buttonText),
                url: videoButtonURL ? (autoValueIsAutoValue(videoButtonURL) ? undefined : videoButtonURL) : undefined
            }),
            ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                hasSourceURL: true,
                ctaButton: ctaButton,
            }),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration(action.settings, true),
            ...restSettings
        }
    },
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            actionSettings.url = ''
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    resolveMainLink: (actionSettings, htmlSiteInfo, viewerLang, bymoPageProps, viewerPagesRoutesManager) => {
        const normalizedURL = doppeBymoPageVideoSourceResolveVideoUrl(actionSettings.url);
        return typeof actionSettings.url === 'string'  ? normalizedURL : doppeActionCtaButtonsResolveMainURL(actionSettings, {
            htmlSiteInfo: htmlSiteInfo,
            viewerLang: viewerLang,
            sourceURL: normalizedURL,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager
        }) ?? null;
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            url: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Url, BymoPagePropsContentSettingSemanticMeaning.EntityPageUrl),
            title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            videoImage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.SingleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia)
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        }
    },
    actionListItemSettingsMetadata: false,
    isWidgetOpener: true,
    isLinkOpener: true,
    canRenderInPage: true,
})

